import axios from 'axios';
import { haciendaClient } from './haciendaClient';
import type {
  CreateElectronicInvoiceParams,
  ElectronicInvoiceResponse,
  HaciendaApiResponse,
  HaciendaInvoiceStatusResponse,
  ResendInvoiceEmailResponse,
  ForwardInvoiceEmailResponse,
  CreateElectronicDocumentFormTokenResponse,
  CompanyRegimeResponse,
  CrProvinciasTree,
  LocationsAllResponse,
} from './haciendaTypes';
import { sendSupportEmail } from '../supportEmail/supportEmailService';
import { openPdfBlob } from '@/utils/nativeFileUtils';
import { getCurrentOrganization } from '@/lib/organizationBridge';

export const createElectronicInvoice = async (
  params: CreateElectronicInvoiceParams
): Promise<ElectronicInvoiceResponse> => {
  try {
    const response = await haciendaClient.post<HaciendaApiResponse>('/invoices', params);
    
    if (response.status === 201) {
      return {
        success: true,
        data: response.data,
      };
    }
    
    return {
      success: false,
      error: 'Unexpected response from Hacienda microservice',
    };
  } catch (error) {
    const organization = getCurrentOrganization();
    // send email to support with the error
    await sendSupportEmail({
      subject: 'Error in Hacienda microservice',
      message: error.message,
      source: 'haciendaService.getElectronicInvoice',
      metadata: {
        organizationId: organization?.id,
        organizationName: organization?.name,
        organizationSlug: organization?.slug,
      },
      level: 'error',
      error: error,
    });
    if (axios.isAxiosError(error)) {
      return {
        success: false,
        error: error.response?.data?.message || error.message,
      };
    }
    return {
      success: false,
      error: 'An unexpected error occurred',
    };
  }
};

export const getElectronicInvoiceStatus = async (
  invoiceId: string
): Promise<HaciendaInvoiceStatusResponse> => {
  const response = await haciendaClient.get<HaciendaInvoiceStatusResponse>(`/invoices/${invoiceId}`);
  return response.data;
};

export const resendInvoiceEmail = async (
  invoiceId: string
): Promise<ResendInvoiceEmailResponse> => {
  try {
    const response = await haciendaClient.post(`/invoices/${invoiceId}/resend-email`);
    if (response.status === 200) {
      return {
        success: true,
      };
    }else{
      return {
        success: false,
        error: response.data.message,
      };
    }
  } catch (error) {
    return {
      success: false,
      error: 'An unexpected error occurred while resending invoice email',
    };
  }
};

export const forwardInvoiceEmail = async (
  invoiceId: string,
  emails: string[]
): Promise<ForwardInvoiceEmailResponse> => {
  try {
    const response = await haciendaClient.post<{ sent_to?: string[] }>(
      `/invoices/${invoiceId}/forward-email`,
      { emails }
    );
    if (response.status === 200) {
      return {
        success: true,
        sent_to: response.data?.sent_to ?? emails,
      };
    }
    return {
      success: false,
      error: (response.data as { error?: string; message?: string })?.error
        ?? (response.data as { message?: string })?.message,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as { error?: string; message?: string } | undefined;
      return {
        success: false,
        error: data?.error ?? data?.message ?? error.message,
      };
    }
    return {
      success: false,
      error: 'An unexpected error occurred while forwarding invoice email',
    };
  }
};

export const openInvoicePdf = async (invoiceId: string): Promise<void> => {
  try {
    const response = await haciendaClient.get(`/invoices/${invoiceId}/pdf`, {
      responseType: 'blob',
    });
    await openPdfBlob(response.data as Blob, `invoice-${invoiceId}.pdf`);
  } catch (error) {
    console.error('Error opening invoice PDF:', error);
    throw error;
  }
};

/**
 * Read the company's tax regime from the Hacienda microservice (single source of
 * truth). Keyed by the company API key on `haciendaClient`; no companyId needed.
 */
export const getCompanySimplifiedRegime = async (): Promise<boolean> => {
  const response = await haciendaClient.get<CompanyRegimeResponse>('/companies/regime');
  return !!response.data?.isSimplifiedRegime;
};

/** Full CR territorial tree (`GET /locations/all`). Filter cascade client-side. */
export const getLocationsAll = async (): Promise<CrProvinciasTree> => {
  const response = await haciendaClient.get<LocationsAllResponse>('/locations/all');
  return response.data?.provincias ?? {};
};

/** Update the company's tax regime (single source of truth in the microservice). */
export const updateCompanySimplifiedRegime = async (
  isSimplifiedRegime: boolean
): Promise<boolean> => {
  const response = await haciendaClient.put<CompanyRegimeResponse>(
    '/companies/regime',
    { isSimplifiedRegime }
  );
  return !!response.data?.isSimplifiedRegime;
};

export const getCreateElectronicDocumentFormToken =
  async (): Promise<CreateElectronicDocumentFormTokenResponse> => {
  try {
    // Regime is no longer sent: the Hacienda microservice is the source of truth
    // (companies.is_simplified_regime) and resolves it server-side.
    const response = await haciendaClient.post<CreateElectronicDocumentFormTokenResponse>(
      '/invoices/create-token',
      {}
    );
    const token = response.data?.token;
    if (!token) {
      throw new Error('Token not received from API');
    }
    return {
      token,
      expiresIn: response.data?.expiresIn,
      refreshToken: response.data?.refreshToken,
      refreshExpiresIn: response.data?.refreshExpiresIn,
    };
  } catch (error) {
    console.warn('Error calling Hacienda microservice:', error);
    throw error;
  }
};

/**
 * @param targetWindow Optional tab opened synchronously on user click (before async
 * token fetch). Browsers block `window.open(url)` after `await`; navigate this tab instead.
 */
export const openCreateElectronicDocumentForm = (
  token: string,
  refreshToken: string | undefined,
  targetWindow?: Window | null
): void => {
  const factoraUrl = import.meta.env.VITE_FACTORA_URL as string | undefined;
  if (factoraUrl?.trim()) {
    const base = factoraUrl.trim().replace(/\/+$/, '');
    const hashParams = new URLSearchParams({ token });
    if (refreshToken) {
      hashParams.set('refresh', refreshToken);
    }
    const url = `${base}/embed/invoices#${hashParams.toString()}`;
    if (targetWindow && !targetWindow.closed) {
      targetWindow.location.href = url;
      return;
    }
    const opened = window.open(url, '_blank', 'noopener,noreferrer');
    if (!opened) {
      throw new Error(
        'No se pudo abrir Factora. Permite ventanas emergentes para este sitio e intenta de nuevo.'
      );
    }
    return;
  }

  const baseURL = haciendaClient.defaults.baseURL;
  if (!baseURL) {
    throw new Error('Hacienda client base URL is not configured');
  }
  const formUrl = `${baseURL}/invoices/invoices-list-form`;
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = formUrl;
  form.target = '_blank';

  const tokenInput = document.createElement('input');
  tokenInput.type = 'hidden';
  tokenInput.name = 'token';
  tokenInput.value = token;
  form.appendChild(tokenInput);

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};